import { Dispatch } from "redux";
import api from "../../api";
import { addMessageStatus } from "../ui/action";
import NavigationHelper from "../../components/utils/NavigationHelper";
import {
    WhoIsPayingAction,
    UpdateDebitorAction,
    UpdateTimeAndPlaceAction,
    UpdateCarAction,
    LoadOrderAction,
    UpdateImageAction,
    ClearOrderAction,
    UpdateCustomerAction,
    UpdateAdditionalAction,
    UpdateItemsAction,
    UpdatePurchaseItemsAction,
    UpdatePurchaseItemPricesAction,
    SetProductDiscountAction,
    SetProductAmountAction,
    SetProductPriceAction,
    RemoveProductAction,
    UpdateOrderAction,
    RemoveImagesAction,
    AddProductAction,
    AddProductsAction,
    SetProductNameAction,
    UpdateCustomerCityFromZipAction,
    UpdateComplaintAction
} from "./ActionInterfaces";
import {
    orderToNotificationModel,
    getRepairTypeFromOrder
} from "../../components/utils/smsHelper";
import { orderToOrderConfirmationModel } from "../../components/utils/emailHelper";
import {
    getCorrectItemPrices,
    updateDebitorPrices
} from "../stateUtils/itemPriceHelper";
import { sendSms } from "../../state/sms/action";
import { MessageBarType } from "@fluentui/react";
import { UpdateProductListAction, isProductRequestProcessing, productSignalController } from "../product/action";
import * as uniqId from "uniqid";
import * as cloneDeep from 'lodash';
import { FreeCarSku } from "../../components/typings";

export const updateItems = (items: PurchaseItem[]): UpdateItemsAction => ({
    type: "UPDATE_ITEMS",
    items
});

export const updatePurchaseItems = (items: PurchaseItem[]): UpdatePurchaseItemsAction => ({
    type: "UPDATE_PURCHASE_ITEMS",
    items
});
export const updatePurchaseItemPrices = (items: PurchaseItem[]): UpdatePurchaseItemPricesAction => ({
    type: "UPDATE_PURCHASE_ITEM_PRICES",
    items
});

let fetchingDebitor = false;
let debitorSearch: Debitor | undefined = undefined;

export const updateDebitorFromWhoIsPaying = (
    whoIsPaying: WhoIsPaying,
    order: Order,
    updatePrice: boolean,
    clearBookingTime:boolean
) => async (dispatch: Dispatch) => {
    let debitor = order.debitor;
    debitorSearch = debitor;
    if (fetchingDebitor) return;
    fetchingDebitor = true;
    let retDebitor: Debitor | undefined = undefined;
    try {
        do {
            debitor = debitorSearch;
            if (whoIsPaying === "INSURANCE" && order.car.insurance) {
                retDebitor = (
                    await api.DebitorApi.getDebitor(order.car.insurance, "NAME")
                )[0];
            } else if (
                whoIsPaying.endsWith("CASH") &&
                order.timeAndPlace.department
            ) {
                const departmentDebitor = (
                    await api.DebitorApi.getDebitor(
                        order.timeAndPlace.department.debitorId,
                        "NAV"
                    )
                )[0];
                retDebitor = {
                    ...order.customer,
                    debitorName: departmentDebitor.debitorName,
                    cvr: departmentDebitor.cvr,
                    no: departmentDebitor.no
                };
            } else if (whoIsPaying === "LEASING" && order.car.leaser) {
                retDebitor = (
                    await api.DebitorApi.getDebitor(order.car.leaser, "NAME")
                )[0];
            } else {
                retDebitor = debitor;
            }
        } while (debitor != debitorSearch);
        debitorSearch = undefined;
        if (retDebitor) {
            const updateDebitoraction: UpdateDebitorAction = {
                type: "UPDATE_DEBITOR",
                debitor: retDebitor,
                whoIsPaying
            };
            dispatch(updateDebitoraction);
            if (updatePrice) {
                const items = await updateDebitorPrices(retDebitor, {...order, additionalData: {...order.additionalData, whoIsPaying}});
                dispatch(updateItems(items));
            }
        }
        dispatch(whoIsPayingChange(whoIsPaying));
        if(whoIsPaying === "CASH" && clearBookingTime)
        {
            updateTimeAndPlace({
                timeSlotId: undefined,
                from: undefined,
                to: undefined
            })(dispatch);
         
        }
        fetchingDebitor = false;
    } catch (ex) {
        fetchingDebitor = false;
    }
};

export const updateDebitor = (
    debitor: Partial<Debitor>,
    order: Order
) => async (dispatch: Dispatch) => {
    await updateDebitorPrices(debitor, order);
    dispatch(updateItems(order.items));
    const updateDebitoraction: UpdateDebitorAction = {
        type: "UPDATE_DEBITOR",
        debitor
    };
    dispatch(updateDebitoraction);
};

export const setDebitor = (debitor: Debitor, order: Order) => async (
    dispatch: Dispatch
) => {
    var items = await updateDebitorPrices(debitor, order);
    dispatch(updateItems(items));
    const updateDebitoraction: UpdateDebitorAction = {
        type: "UPDATE_DEBITOR",
        debitor
    };
    dispatch(updateDebitoraction);
};

export const updateOrderDepartment = (
    department: Department,
    shouldUpdateDebitor: boolean
) => async (dispatch: Dispatch) => {
    const action: UpdateTimeAndPlaceAction = {
        type: "UPDATE_TIME_AND_PLACE",
        timeAndPlace: { department },
        worker: ""
    };
    dispatch(action);
    if (shouldUpdateDebitor) {
        //TODO Find debitor from department
    }
};

export const removeProduct = (itemNo: string): RemoveProductAction => ({
    type: "REMOVE_PRODUCT",
    itemNo
});


export const updateCarFromReg = (reg: string) => async (dispatch: Dispatch) => {
    try {
        // Clear products from the previous car before looking up the new reg
        if (isProductRequestProcessing) {
            productSignalController.abort();
        }
        dispatch({ type: "UPDATE_PRODUCT_LIST", productGroups: [], merge: false } as UpdateProductListAction);

        const car = await api.CarAPI.getInformationFromReg(reg);
        const action: UpdateCarAction = { type: "UPDATE_CAR", car };
        dispatch(action);

    } catch (ex) {
        const statusMessage: StatusMessage = {
            messageType: MessageBarType.error,
            message: `Api: "getInformationFromReg" failed with input ${reg}`,
            dismissTimer: 5000
        };

        addMessageStatus(statusMessage)(dispatch);

        throw ex;
    }
};

export const postOrderFromState = (order: Order) => async (
    dispatch: Dispatch
) => {
    try {
        const orderStatus = order.additionalData.orderStatus;
        const items = getCorrectItemPrices(order.items);
        const postOrder = await api.OrderAPI.postOrder({
            ...order,
            items: items,
            additionalData:{...order.additionalData, orderStatus: orderStatus}
        });
        const action: UpdateOrderAction = {
            type: "UPDATE_ORDER",
            order: postOrder
        };
        dispatch(action);
        const notificationType = getRepairTypeFromOrder(postOrder);

        if ( postOrder.customer.allowSms && postOrder.customer.phone) 
        {
            if (postOrder.additionalData.orderStatus !== "OFFER")
            {
                const sms = orderToNotificationModel(postOrder, notificationType);
                sendSms(sms)(dispatch);
            }
        }

        if (postOrder.customer.allowEmail && postOrder.customer.email) {
            try {
                if (
                    order.additionalData.orderStatus === "OFFER" ||
                    order.additionalData.orderStatus === "OFFER_REQUEST"
                ) {
                    await api.EmailApi.sendOfferEmail({
                        ...postOrder,
                        items: items
                    });
                    const statusMessage: StatusMessage = {
                        messageType: MessageBarType.success,
                        message: `Tilbuds mail sent successfully`,
                        dismissTimer: 5000
                    };
                    addMessageStatus(statusMessage)(dispatch);
                } else {
                    await api.EmailApi.sendConfirmationEmail(orderToOrderConfirmationModel(postOrder, notificationType));
                    const statusMessage: StatusMessage = {
                        messageType: MessageBarType.success,
                        message: `Email sent successfully`,
                        dismissTimer: 5000
                    };
                    addMessageStatus(statusMessage)(dispatch);
                }
            } catch {
                const statusMessage: StatusMessage = {
                    messageType: MessageBarType.error,
                    message: `Failed to send email`,
                    dismissTimer: 5000
                };

                addMessageStatus(statusMessage)(dispatch);
            }
        }

        const statusMessage: StatusMessage = {
            messageType: MessageBarType.success,
            message: `Order accepted`,
            dismissTimer: 5000
        };

        addMessageStatus(statusMessage)(dispatch);
        NavigationHelper.pushRoute("RECEIPT");
    } catch (ex) {
        const statusMessage: StatusMessage = {
            messageType: MessageBarType.error,
            message: `Api: "postOrder" failed`,
            dismissTimer: 5000
        };
        addMessageStatus(statusMessage)(dispatch);

        throw ex;
    }
};

export const updateCarId = (car: Car) => async (dispatch: Dispatch) => {
    try {
        const carId = await api.CarAPI.getCarIdFromCar(car);

        const action: UpdateCarAction = {
            type: "UPDATE_CAR",
            car: { ...car, carId }
        };
        dispatch(action);
    } catch (ex) {
        const statusMessage: StatusMessage = {
            messageType: MessageBarType.error,
            message: `Api: "getCarId" failed`,
            dismissTimer: 5000
        };

        addMessageStatus(statusMessage)(dispatch);

        throw ex;
    }
};

export const loadOrder = (orderId: string) => async (dispatch: Dispatch) => {
    try {
        const order = await api.OrderAPI.getOrder(orderId);
        const action: LoadOrderAction = { type: "LOAD_ORDER", order };
        dispatch(action);
    } catch (ex) {
        const statusMessage: StatusMessage = {
            messageType: MessageBarType.error,
            message: `Loading order failed`,
            dismissTimer: 5000
        };

        addMessageStatus(statusMessage)(dispatch);

        throw ex;
    }
};


export const updateMobileServiceProduct = (
    isMobileService: boolean,
    debitorNo: string,
    type: AssignmentType,
    whoIsPaying: WhoIsPaying, 
    insurance?: string
) => async (dispatch: Dispatch) => {
    const productUD = type === "REPAIR" ? "REPUD" : "UD";
    if (isMobileService) {
        let purchaseItem: PurchaseItem | null = null;
        const product = await api.ProductAPI.getProduct(productUD);
        product.amount = 1;
        if (debitorNo) {
            const debitorPrices = await api.DebitorApi.getDebitorPrices(
                debitorNo,
                [{ productNo: productUD, amount: 1 }],
                whoIsPaying, 
                insurance
            );
            purchaseItem  = debitorPrices[0]; 
        }
        if (purchaseItem)
        {
            const action: AddProductAction = { type: "ADD_PRODUCT", product: purchaseItem };
            dispatch(action);
        }
;
    } else {
        const action = removeProduct(productUD);
        dispatch(action);
    }
};

export const addProducts = (
    products: PurchaseItem[], 
    debitorNo: string, 
    whoIsPaying: WhoIsPaying, 
    insurance?: string,
    orderType?: string,
    firstRegistrationDate?: string
) => async (
    dispatch: Dispatch
) => {
    if (!debitorNo || debitorNo === "") {
        const action: AddProductsAction = { type: "ADD_PRODUCTS", products };
        dispatch(action);
    } else {
        const purchaseItems = await api.DebitorApi.getDebitorPrices(
            debitorNo,
            products
                .map<ProductPriceRequest>(p => ({
                    productNo: p.no,
                    substituteNo: p.substituteNo,
                    amount: p.amount,
                    isMainItem: p.isMainItem
                })),
            whoIsPaying, 
            insurance,
            orderType,
            firstRegistrationDate
        );
        dispatch(updatePurchaseItems(purchaseItems));
    }
};

export const addProductsAndUpdateOrder = (
    order: Order,
    products: PurchaseItem[],
    debitorNo: string
) => async (dispatch: Dispatch) => {

    if (!products || products.length === 0) {
        return;
    }

    let orderCopy = cloneDeep(order).cloneDeepWith();
    let debitorPrices: PurchaseItem[] = []
    if (debitorNo || debitorNo !== "") {
       debitorPrices = await api.DebitorApi.getDebitorPrices(
            debitorNo,
            products
                .map<ProductPriceRequest>(p => ({
                    productNo: p.no,
                    substituteNo: p.substituteNo,
                    amount: p.amount
                })),
            order.additionalData.whoIsPaying, 
            order.car.insurance,
            order.additionalData.orderType,
            order.car.firstRegistrationDate
        );
         
    }
    debitorPrices.forEach(product => {
        const index = orderCopy.items.findIndex(
            e => e.itemNo === product.no
        );
        if (index > -1) {
            orderCopy.items[index].amount = orderCopy.items[index].amount + product.amount;
        } else {
            orderCopy.items.push(product);
        }
    });
    const postOrder = await api.OrderAPI.updateOrder(orderCopy);
    const action: UpdateOrderAction = {
        type: "UPDATE_ORDER",
        order: postOrder
    };
    dispatch(action);
    const orderUpdateMessage: StatusMessage = {
        messageType: MessageBarType.success,
        message: `OrderUpdated`,
        dismissTimer: 5000
    };
    addMessageStatus(orderUpdateMessage)(dispatch);
};

export const updateCustomerCityFromZip = (zipCode: string) => async (
    dispatch: Dispatch
) => {
    const city = await api.AddressApi.getCityByZip(zipCode);
    if (city.length === 0) {
        return;
    }

    const action: UpdateCustomerCityFromZipAction = {
        type: "UPDATE_CUSTOMER_CITY_FROM_ZIP",
        city
    };

    dispatch(action);
};

export const createMoreSaleOrder = (order: Order) => async (  dispatch: Dispatch) => {
    try {
        const orderId = order.additionalData.orderId;
        if (orderId) {
            const newOrder = await api.OrderAPI.createMoreSaleOrder(orderId);
            window.location.href = `/worksheet/${newOrder.additionalData.orderId}`;
        }
    } catch (ex) {
        const statusMessage: StatusMessage = {
            messageType: MessageBarType.error,
            message: `Api: "Create Moresale order" failed`,
            dismissTimer: 5000
        };

        addMessageStatus(statusMessage)(dispatch);

        throw ex;
    }
};

export const clearOrder = (): ClearOrderAction => ({ type: "CLEAR_ORDER" });

export const updateCustomer = (
    customer: Partial<Customer>
): UpdateCustomerAction => ({ type: "UPDATE_CUSTOMER", customer });

export const updateAdditional = (
    additional: AdditionalData
): UpdateAdditionalAction => ({ type: "UPDATE_ADDITIONAL", additional });

export const updateCar = (car: Car): UpdateCarAction => ({
    type: "UPDATE_CAR",
    car
});

export const setDiscount = (itemNo: string, discount: number): SetProductDiscountAction => ({ type: "SET_PRODUCT_DISCOUNT", itemNo, discount });

export const setProductAmount = (
    itemNo: string,
    amount: number
): SetProductAmountAction => ({
    type: "SET_ORDER_PRODUCT_AMOUNT",
    itemNo,
    amount
});

export const setProductPrice = (
    itemNo: string,
    price: number
): SetProductPriceAction => ({
    type: "SET_ORDER_PRODUCT_PRICE",
    itemNo,
    price
});

export const setProductName = (
    itemNo: string,
    name: string
): SetProductNameAction => ({ type: "SET_ORDER_PRODUCT_NAME", itemNo, name });

export const updateTimeAndPlace = (timeAndPlace: Partial<TimeAndPlace>, worker?: string) => async (  dispatch: Dispatch) => {
    const action : UpdateTimeAndPlaceAction = ({ type: "UPDATE_TIME_AND_PLACE", timeAndPlace, worker });
    dispatch(action);
    if(timeAndPlace.loanCar)
    {
        
        const freecarSku: PurchaseItem[] = [
            {
                no: FreeCarSku.CAR_ONE,
                name: "INKL. GRATIS LÅNEBIL",
                amount: 1,
                discountPrice: 0,
                itemNo: FreeCarSku.CAR_ONE,
                description: "",
                isService: true,
                twoPersonJob: false,
                isMainItem: true,
                previouslyPurchased: false,
                itemPrice: 0,
                status: [],
                type: 0,
                price: 0,
                discountPercent: 0,
                discountAmount: 0,
                itemType: "",
                tipsGenerel: "",
                tipsCalibrating: "",
                tipsMounting: "",
                euroCode: "",
                description2: "",
                description3: "",
                description4: ""
            }
        ];
        addProducts(freecarSku, "","INSURANCE", "")(dispatch);
    }
    else
    {
        dispatch(removeProduct(FreeCarSku.CAR_ONE));
    }
}

export const getAnydayOrder = (orderId: string) => async (dispatch: Dispatch) => 
{
    const anyday = await api.AnydayApi.order(orderId);
    if (anyday)
    {
        dispatch({ type: "ANYDAY_ORDER", anydayOrder: anyday });
    }
};
   

export const updateOrder = (order: Order): UpdateOrderAction => ({ type: "UPDATE_ORDER", order });

export const removeImages = (images: string[], imageType: ImageType): RemoveImagesAction => ({ type: "REMOVE_IMAGES", images, imageType });

export const whoIsPayingChange = (whoIsPaying: WhoIsPaying): WhoIsPayingAction => ({ type: "WHO_IS_PAYING", whoIsPaying });

export const updateComplaint = (complaint: Partial<Complaint>): UpdateComplaintAction => ({ type: "UPDATE_COMPLAINT", complaint });
